import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Loader2, Sparkles, CheckCircle2 } from "lucide-react";

export const CategoryDescriptionGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalProcessed, setTotalProcessed] = useState(0);
  const queryClient = useQueryClient();

  const { data: stats } = useQuery({
    queryKey: ['category-description-stats'],
    queryFn: async () => {
      const { count: totalCount } = await supabase
        .from('categories')
        .select('*', { count: 'exact', head: true });

      const { count: missingCount } = await supabase
        .from('categories')
        .select('*', { count: 'exact', head: true })
        .or('description.is.null,description.eq.');

      return { 
        total: totalCount || 0, 
        missing: missingCount || 0,
        completed: (totalCount || 0) - (missingCount || 0)
      };
    },
  });

  const generateBatch = async () => {
    const { data, error } = await supabase.functions.invoke('generate-category-descriptions', {
      body: { batchSize: 10 }
    });

    if (error) throw error;
    return data;
  };

  const mutation = useMutation({
    mutationFn: generateBatch,
    onSuccess: (data) => {
      setTotalProcessed(prev => prev + (data.processed || 0));
      queryClient.invalidateQueries({ queryKey: ['category-description-stats'] });
    },
  });

  const handleGenerateAll = async () => {
    if (!stats?.missing) {
      toast.info("All categories already have descriptions!");
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setTotalProcessed(0);

    try {
      const totalBatches = Math.ceil(stats.missing / 10);
      
      for (let i = 0; i < totalBatches; i++) {
        await mutation.mutateAsync();
        setProgress(((i + 1) / totalBatches) * 100);
        
        // Small delay between batches
        if (i < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      toast.success(`Generated descriptions for ${totalProcessed} categories!`);
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate descriptions');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Category Description Generator
          </h3>
          <p className="text-sm text-muted-foreground">
            Generate unique, SEO-friendly descriptions for categories using AI. This helps solve duplicate content issues.
          </p>
        </div>

        {stats && (
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Total Categories</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Need Descriptions</p>
              <p className="text-2xl font-bold text-orange-500">{stats.missing}</p>
            </div>
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Completed</p>
              <p className="text-2xl font-bold text-green-500 flex items-center gap-2">
                {stats.completed}
                {stats.missing === 0 && <CheckCircle2 className="h-5 w-5" />}
              </p>
            </div>
          </div>
        )}

        {isGenerating && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Generating descriptions...</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} />
            <p className="text-xs text-muted-foreground">
              Processed: {totalProcessed} categories
            </p>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            onClick={handleGenerateAll}
            disabled={isGenerating || !stats?.missing}
            className="flex-1"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate All Descriptions
              </>
            )}
          </Button>
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <p>• Processes 10 categories at a time</p>
          <p>• Each description is unique and contextual</p>
          <p>• Safe to run multiple times (only updates missing descriptions)</p>
        </div>
      </div>
    </Card>
  );
};
